import type { IconName } from "@repo/fika-design-system/icon"
import { createQuery } from "@urql/solid"
import { createMemo, createSignal, For, Show } from "solid-js"
import { urqlClient } from "@/context/graphql-provider"
import { createSession } from "@/context/session-provider"
import { Box } from "@/designSystem/components/box"
import { Button } from "@/designSystem/components/button"
import { Dialog } from "@/designSystem/components/dialog"
import { Form } from "@/designSystem/components/form"
import { Icon } from "@/designSystem/components/icon"
import { Text } from "@/designSystem/components/text"
import { TextField } from "@/designSystem/components/text-field"
import { MainLayout } from "@/designSystem/layouts/main-layout"
import { Navigation } from "@/designSystem/patterns/navigation"
import {
  createFragment,
  type FragmentOf,
  graphql,
  type ResultOf,
} from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import type { TranslationFunctions } from "@/i18n/i18n-types"
import {
  createMutationWithErrorHandling,
  ErrorFragment,
} from "@/lib/graphql-mutation-helpers"
import { toast } from "@/lib/toast"
import { createBlogRouter } from "@/routes/posts-router"
import { LocaleForm, UserLocaleFragment } from "./_locale-form"
import { ProfileForm, UserProfileFragment } from "./_profile-form"
import { deleteAccountConfirmDescriptionLink } from "./page.css"

type SocialI18nKey = keyof TranslationFunctions["settings"]["social"]

const UserSocialFragment = graphql(`
  fragment UserSocialFragment on UserInterface {
    id
    socialX
    socialInstagram
    socialTiktok
    socialYoutube
    socialFacebook
    socialLinkedin
    socialBluesky
    socialBooks
    socialWebsite
    socialSoundcloud
    socialBandcamp
  }
`)

const UserAuthFragment = graphql(`
  fragment UserAuthFragment on Me {
    id
    email
  }
`)

const CurrentUserSettingsQuery = graphql(
  `query CurrentUserSettings {
    me {
      id
      publication {
        id
        name
      }
      ...UserProfileFragment
      ...UserSocialFragment
      ...UserLocaleFragment
      ...UserAuthFragment
    }
  }`,
  [
    UserProfileFragment,
    UserSocialFragment,
    UserLocaleFragment,
    UserAuthFragment,
  ],
)

export function preloadSettings() {
  return urqlClient.query(CurrentUserSettingsQuery, {}).toPromise()
}

export function Settings() {
  const { LL } = useI18nContext()
  const [result] = createQuery({
    query: CurrentUserSettingsQuery,
  })

  return (
    <MainLayout
      hue="orange"
      header={<Navigation hue="orange" title={LL().settings.title()} />}
      query={result}
    >
      {data => (
        <Show when={data?.me}>
          {user => (
            <Box gap="24">
              <Show when={user().publication?.name}>
                <ProfileForm user={user()} />
                <SocialForm user={user()} />
              </Show>
              <LocaleForm user={user()} />
              <LogoutForm user={user()} />
              <DeleteAccountForm />
            </Box>
          )}
        </Show>
      )}
    </MainLayout>
  )
}

type SocialData = ResultOf<typeof UserSocialFragment>
type SocialKey = keyof SocialData & string

function SocialForm(props: { user: FragmentOf<typeof UserSocialFragment> }) {
  const user = createFragment(UserSocialFragment, () => props.user)
  const { LL } = useI18nContext()
  const UpdateCurrentUserMutation = graphql(
    `mutation UpdateCurrentUser($input: UpdateUserInput!) {
      updateCurrentUser(input: $input) {
        ... on MutationUpdateCurrentUserSuccess {
          data {
            ...UserSocialFragment
          }
        }
        ...ErrorFragment
      }
    }`,
    [UserSocialFragment, ErrorFragment],
  )
  const [updateUserState, updateUser] = createMutationWithErrorHandling(
    UpdateCurrentUserMutation,
    "updateCurrentUser",
  )

  const socialPlatforms = [
    { key: "socialX", icon: "twitter", i18nKey: "social_x" },
    { key: "socialInstagram", icon: "instagram", i18nKey: "social_instagram" },
    { key: "socialTiktok", icon: "link", i18nKey: "social_tiktok" },
    { key: "socialYoutube", icon: "youtube", i18nKey: "social_youtube" },
    { key: "socialFacebook", icon: "facebook", i18nKey: "social_facebook" },
    { key: "socialLinkedin", icon: "linkedin", i18nKey: "social_linkedin" },
    { key: "socialBluesky", icon: "butterfly", i18nKey: "social_bluesky" },
    { key: "socialBooks", icon: "book-open-text", i18nKey: "social_books" },
    { key: "socialWebsite", icon: "globe", i18nKey: "social_website" },
    { key: "socialSoundcloud", icon: "music", i18nKey: "social_soundcloud" },
    { key: "socialBandcamp", icon: "music", i18nKey: "social_bandcamp" },
  ] as const satisfies readonly {
    key: SocialKey
    icon: IconName
    i18nKey: SocialI18nKey
  }[]

  const initialSocials = socialPlatforms.reduce(
    (acc, { key }) => {
      acc[key] = user()[key] ?? ""
      return acc
    },
    {} as Record<SocialKey, string>,
  )

  const [socials, setSocials] = createSignal(initialSocials)

  const isDirty = createMemo(() =>
    socialPlatforms.some(({ key }) => socials()[key] !== (user()[key] ?? "")),
  )

  async function handleSubmit() {
    const input = socialPlatforms.reduce(
      (acc, { key }) => {
        acc[key] = socials()[key] || null
        return acc
      },
      {} as Record<string, string | null>,
    )

    const state = await updateUser({ input })

    if (state.success) {
      toast.success(LL().settings.informationSaved())
    }
  }
  const labels = createMemo(() => {
    return {
      social_x: LL().settings.social.social_x.label(),
      social_instagram: LL().settings.social.social_instagram.label(),
      social_tiktok: LL().settings.social.social_tiktok.label(),
      social_youtube: LL().settings.social.social_youtube.label(),
      social_facebook: LL().settings.social.social_facebook.label(),
      social_linkedin: LL().settings.social.social_linkedin.label(),
      social_bluesky: LL().settings.social.social_bluesky.label(),
      social_books: LL().settings.social.social_books.label(),
      social_website: LL().settings.social.social_website.label(),
      social_soundcloud: LL().settings.social.social_soundcloud.label(),
      social_bandcamp: LL().settings.social.social_bandcamp.label(),
    }
  })

  const placeholders = createMemo(() => {
    return {
      social_x: LL().settings.social.social_x.placeholder(),
      social_instagram: LL().settings.social.social_instagram.placeholder(),
      social_tiktok: LL().settings.social.social_tiktok.placeholder(),
      social_youtube: LL().settings.social.social_youtube.placeholder(),
      social_facebook: LL().settings.social.social_facebook.placeholder(),
      social_linkedin: LL().settings.social.social_linkedin.placeholder(),
      social_bluesky: LL().settings.social.social_bluesky.placeholder(),
      social_books: LL().settings.social.social_books.placeholder(),
      social_website: LL().settings.social.social_website.placeholder(),
      social_soundcloud: LL().settings.social.social_soundcloud.placeholder(),
      social_bandcamp: LL().settings.social.social_bandcamp.placeholder(),
    }
  })

  return (
    <Form
      title={LL().settings.social.title()}
      description={LL().settings.social.description()}
      hue="orange"
      iconName="user"
      onSubmit={handleSubmit}
    >
      <For each={socialPlatforms}>
        {({ key, icon, i18nKey }) => (
          <TextField
            value={socials()[key]}
            onChange={v => setSocials(prev => ({ ...prev, [key]: v }))}
          >
            <TextField.Label>
              <Box flexDirection="row" alignItems="center" gap="8">
                <Icon name={icon} size="18" color="sand500" />
                {labels()[i18nKey]}
              </Box>
            </TextField.Label>
            <TextField.Input type="url" placeholder={placeholders()[i18nKey]} />
          </TextField>
        )}
      </For>
      <Form.Save
        hue="orange"
        disabled={!isDirty()}
        active={updateUserState.fetching}
        label={
          updateUserState.fetching ? LL().common.saving() : LL().common.save()
        }
      />
    </Form>
  )
}

function LogoutForm(props: { user: FragmentOf<typeof UserAuthFragment> }) {
  const user = createFragment(UserAuthFragment, () => props.user)
  const { LL } = useI18nContext()
  const { actions } = createSession()
  const { signOut, isSigningOut } = actions

  async function handleSignOut() {
    await signOut()
  }

  return (
    <Form
      title={LL().settings.authenticationTitle()}
      description={LL().settings.authenticationDescription()}
      hue="orange"
      iconName="lock"
    >
      <Box flexDirection="column" gap="16">
        <Text fontSize="s" color="sand600">
          {LL().settings.signedInAs({ email: user().email })}
        </Text>
        <Box flexDirection="column" alignItems="flex-end">
          <Button
            kind="solid"
            hue="orange"
            onClick={handleSignOut}
            disabled={isSigningOut()}
          >
            {isSigningOut()
              ? LL().settings.signingOut()
              : LL().settings.signOut()}
          </Button>
        </Box>
      </Box>
    </Form>
  )
}

function DeleteAccountForm() {
  const { LL } = useI18nContext()
  const { actions } = createSession()
  const { signOut } = actions
  const { paths } = createBlogRouter()

  const [isDialogOpen, setIsDialogOpen] = createSignal(false)
  const [confirmText, setConfirmText] = createSignal("")

  const [deleteState, deleteUser] = createMutationWithErrorHandling(
    graphql(
      `mutation DeleteCurrentUser {
        deleteCurrentUser {
          ... on MutationDeleteCurrentUserSuccess {
            data
          }
          ...ErrorFragment
        }
      }`,
      [ErrorFragment],
    ),
    "deleteCurrentUser",
  )

  const confirmWord = "DELETE"
  const isConfirmEnabled = createMemo(() => confirmText() === confirmWord)

  async function handleDelete() {
    const result = await deleteUser({})
    if (result.success) {
      await signOut()
    }
  }

  return (
    <Form
      title={LL().settings.deleteAccount.title()}
      description={LL().settings.deleteAccount.description()}
      hue="rose"
      iconName="trash-2"
    >
      <Box flexDirection="column" gap="16">
        <Text fontSize="s" color="rose700">
          {LL().settings.deleteAccount.warning()}
        </Text>
        <Box flexDirection="column" alignItems="flex-end">
          <Button hue="rose" onClick={() => setIsDialogOpen(true)}>
            {LL().settings.deleteAccount.button()}
          </Button>
        </Box>
      </Box>

      <Dialog
        open={isDialogOpen()}
        onOpenChange={open => {
          setIsDialogOpen(open)
          if (!open) setConfirmText("")
        }}
        title={LL().settings.deleteAccount.confirmTitle()}
        footer={
          <>
            <Button
              kind="surface"
              hue="rose"
              onClick={() => setIsDialogOpen(false)}
            >
              {LL().common.cancel()}
            </Button>
            <Button
              hue="rose"
              onClick={handleDelete}
              disabled={!isConfirmEnabled()}
              active={deleteState.fetching}
            >
              {LL().settings.deleteAccount.confirmButton()}
            </Button>
          </>
        }
      >
        <Box flexDirection="column" gap="16">
          <Text fontSize="s" color="sand700">
            {LL().settings.deleteAccount.confirmDescription()}
          </Text>
          <Text fontSize="s" color="sand700">
            {LL().settings.deleteAccount.exportBeforeDeletePrefix()}{" "}
            <a
              href={`${paths().settings()}#publication-export`}
              class={deleteAccountConfirmDescriptionLink}
            >
              {LL().blog.export.title().toLowerCase()}
            </a>{" "}
            {LL().settings.deleteAccount.exportBeforeDeleteSuffix()}
          </Text>
          <TextField value={confirmText()} onChange={setConfirmText}>
            <TextField.Label>
              {LL().settings.deleteAccount.typeToConfirm({ text: confirmWord })}
            </TextField.Label>
            <TextField.Input type="text" />
          </TextField>
        </Box>
      </Dialog>
    </Form>
  )
}
